import { createContext, useContext } from 'react';
import { PlayerState } from '@/types/AudioPlayer';

export interface SimpleMusicPlayerContextValue {
  // Player state
  playerState: PlayerState;
  isPlaying: boolean;
  isPaused: boolean;
  isLoading: boolean;
  hasError: boolean;
  errorMessage: string | null;
  canPlay: boolean;

  // Track info
  currentTime: number;
  duration: number;
  progress: number; // 0-100

  // Player controls
  play: () => Promise<void>;
  pause: () => void;
  togglePlayPause: () => Promise<void>;
  stop: () => void;

  // Volume controls
  setVolume: (volume: number) => void;
  volume: number; // Redux SSOT (userPreferences)

  // Mute controls
  isMuted: boolean;
  setMuted: (muted: boolean) => void;
  toggleMute: () => void;

  // Navigation controls
  nextTrack: () => Promise<void>;
  previousTrack: () => Promise<void>;
  seekTo: (time: number) => void;
  seekToPercent: (percent: number) => void;

  // Track selection
  playTrackAtIndex: (index: number) => Promise<void>;

  // Utility
  retry: () => void;

  // Platform info
  isUsingWebAudio: boolean;
  canControlVolume: boolean; // false on iOS with HTML5 fallback
}

export const SimpleMusicPlayerContext = createContext<SimpleMusicPlayerContextValue | null>(null);

export const useSimpleMusicPlayer = (): SimpleMusicPlayerContextValue => {
  const context = useContext(SimpleMusicPlayerContext);
  if (!context) {
    throw new Error('useSimpleMusicPlayer must be used within a SimpleMusicPlayerProvider');
  }
  return context;
};
